/**
 * `whyThisRule(rule_id, rules)` — derive a redaction-safe, plain-language
 * sentence explaining what kind of decision a shadow-log row represents.
 *
 * Per ADR-0120 §3.3 (Option A, chosen) the sentence is composed
 * CLIENT-SIDE from:
 *   1. the configured rule the dashboard already loaded from
 *      `loadState(site_id)` (matched by `rule_id`); and
 *   2. the catalog entry's `description` for that rule's `type`
 *      (`apps/dashboard/src/views/guardrails/catalog.ts`); and
 *   3. the merchant's OWN configured params for the rule (e.g. the r05
 *      per-currency cap, the r23 validity-window seconds).
 *
 * The sentence is CATEGORICAL — it describes what the rule type
 * detects, optionally narrowed by the merchant's configuration. It NEVER
 * reads anything from the mandate the shadow row references, NEVER
 * echoes a matched secret, and NEVER renders attacker-controlled text.
 * React's default text-node escaping is the baseline; this function
 * returns a plain string and the panel renders it under a `<span>`
 * (no `dangerouslySetInnerHTML`, no markdown). See ADR-0120 §4 security
 * must-hold 1.
 *
 * Indeterminate rule_ids (the r03/r04/r13/r17 set — see
 * `shadow-indeterminate.ts`) get a FIXED sentence per ADR-0120 §3.3
 * that explicitly disclaims the verdict's definitiveness. The shadow
 * eval of those rules runs against stubbed callback surfaces in
 * `apps/edge/src/mandate/dry_run_shadow.ts`, so a "would-have-blocked"
 * verdict is NOT a definitive statement that enforcement would block
 * — synthesising certainty there is the load-bearing honesty invariant
 * the ADR pins.
 *
 * Unknown `rule_id` → a generic fallback string ("This rule's
 * configuration would have rejected the mandate"). Never throws, never
 * returns undefined.
 *
 * This module is the only place the dashboard composes "why" copy.
 * It is pure (no React, no `useC`, no fetches) so the test for the
 * security must-hold (no mandate token leakage) can run as a unit test.
 */

import type { Rule } from '@sill/types';

import { RULES, ruleByType } from './catalog.js';
import { isIndeterminateShadowRule } from './shadow-indeterminate.js';
import type { RuleId } from '@sill/types';

// Re-export for callers that want to assert the fixed indeterminate copy.
export const INDETERMINATE_SENTENCE: string =
  'This rule depends on per-window rate-limit state that shadow evaluation '
  + 'cannot observe. The row is recorded for completeness; real enforcement '
  + 'may or may not block.';

/**
 * Generic fallback for an unknown `rule_id` (a row written by an edge
 * shadow producer for a rule that has since been removed from the
 * merchant's draft AND is not in the static catalog — exceedingly rare
 * but possible across schema rolls). Deliberately does NOT claim more
 * knowledge than we have ("we don't know why" would imply we tried to
 * find out and failed).
 */
export const GENERIC_FALLBACK_SENTENCE: string =
  "This rule's configuration would have rejected the mandate.";

/**
 * Compose a redaction-safe, plain-language sentence describing the
 * rule's intent. Categorical by construction.
 *
 * @param rule_id — the shadow row's `rule_id`. The function looks the
 *   id up in `rules` (the merchant's configured draft) and then in the
 *   static catalog by `rule.type`. NEVER read from the mandate.
 * @param rules — the merchant's configured rules (the same array the
 *   `Guardrails` view already loaded via `loadState(site_id)`). Used to
 *   parameterize the sentence by the merchant's OWN configured params
 *   (e.g. r05 cap + currency). Pass `[]` for an environment with no
 *   loaded draft — the helper falls through to the catalog `description`.
 */
export function whyThisRule(
  rule_id: string,
  rules: readonly Rule[],
): string {
  const configured = rules.find((r) => r.rule_id === rule_id);
  // If the merchant has a configured rule for this id, look up its
  // catalog entry by `type`; otherwise this rule_id is not in the draft
  // (the merchant may have deleted it between shadow write and panel
  // render). In that case we can still try to interpret the rule_id as
  // a catalog `type` slug (e.g. the edge producer wrote `rule_id =
  // 'r05'` for an unconfigured-default eval, which the catalog has an
  // entry for) — the fallback is the same generic sentence.
  const catalog = configured !== undefined
    ? ruleByType(configured.type)
    : RULES.find((r) => r.type === rule_id);

  // Indeterminate honesty (ADR-0120 §3.4): the rate-limit-binding-missing
  // set CANNOT produce a definitive verdict in shadow. Check both the
  // raw `rule_id` AND the resolved catalog type — a configured rule
  // with a merchant-named slug whose TYPE is r03/r04/r13/r17 must still
  // get the honesty sentence, never a synthesised "rule X would have
  // blocked".
  if (
    isIndeterminateShadowRule(rule_id as RuleId)
    || (catalog !== undefined
      && isIndeterminateShadowRule(catalog.type as RuleId))
  ) {
    return INDETERMINATE_SENTENCE;
  }

  if (catalog === undefined) {
    return GENERIC_FALLBACK_SENTENCE;
  }

  // For the params-bearing types, narrow the catalog description with
  // the merchant's OWN configured values. Each branch is pure — it reads
  // only from `configured.params` (never the mandate). When the merchant
  // has no configured rule for this id, fall back to the catalog's
  // unparameterized description.
  if (configured !== undefined) {
    const narrowed = narrowByMerchantParams(configured, catalog.description);
    if (narrowed !== null) {
      return narrowed;
    }
  }

  return catalog.description;
}

/**
 * Append a short merchant-config qualifier to the categorical
 * description for the rule types that carry tunable params. Returns
 * `null` when the rule type has no params worth narrowing — the caller
 * falls back to the unparameterized catalog `description`.
 *
 * Every branch reads ONLY from `rule.params` (the merchant's stored
 * config). NONE reads from a mandate, an envelope, an attacker-supplied
 * field. Numeric formatting only — no string templating against
 * arbitrary content.
 */
function narrowByMerchantParams(
  rule: Rule,
  baseDescription: string,
): string | null {
  switch (rule.params.type) {
    case 'r01': {
      const list = (rule.params as { allowlisted_agent_ids?: string[] })
        .allowlisted_agent_ids ?? [];
      const n = list.length;
      return `${baseDescription} Your allowlist has ${n} agent${
        n === 1 ? '' : 's'
      }; any other agent would be rejected.`;
    }
    case 'r05': {
      const p = rule.params as {
        max_amount_usd?: number;
        caps?: Record<string, number>;
        on_unlisted_currency?: string;
      };
      const entries: Array<[string, number]> = [];
      if (p.caps !== undefined) {
        for (const [code, amount] of Object.entries(p.caps)) {
          if (typeof amount === 'number') entries.push([code, amount]);
        }
      }
      if (
        typeof p.max_amount_usd === 'number'
        && !entries.some(([c]) => c === 'USD')
      ) {
        entries.push(['USD', p.max_amount_usd]);
      }
      if (entries.length === 0) return baseDescription;
      const caps = entries
        .map(([code, amount]) => `${code} ≤ ${amount}`)
        .join(', ');
      const unlistedTail = p.on_unlisted_currency === 'reject'
        ? '; mandates in any other currency are rejected'
        : '';
      return `${baseDescription} Your configured caps: ${caps}${unlistedTail}.`;
    }
    case 'r06': {
      const usd = (rule.params as { daily_cap_usd?: number }).daily_cap_usd;
      if (typeof usd !== 'number') return baseDescription;
      return `${baseDescription} Your configured cap is $${usd} per customer per 24 hours.`;
    }
    case 'r31': {
      // ADR-0151 — cross-agent organization budget cap. Render the window
      // in the most readable unit (days, then hours) so a 24h or 7d window
      // is legible. `window_seconds` is optional; the default is 86400 (24h).
      const p = rule.params as {
        org_budget_usd?: number;
        window_seconds?: number;
      };
      if (typeof p.org_budget_usd !== 'number') return baseDescription;
      const win = typeof p.window_seconds === 'number' ? p.window_seconds : 86_400;
      const windowPhrase =
        win % 86_400 === 0
          ? `${win / 86_400}-day`
          : win % 3_600 === 0
            ? `${win / 3_600}-hour`
            : `${win}-second`;
      return `${baseDescription} Your configured cap is $${p.org_budget_usd} per organization across all of your agents per ${windowPhrase} rolling window.`;
    }
    case 'r07': {
      const p = rule.params as {
        max_auto_approve_usd?: number;
        auto_approve_caps?: Record<string, number>;
      };
      const entries: Array<[string, number]> = [];
      if (p.auto_approve_caps !== undefined) {
        for (const [code, amount] of Object.entries(p.auto_approve_caps)) {
          if (typeof amount === 'number') entries.push([code, amount]);
        }
      }
      if (
        typeof p.max_auto_approve_usd === 'number'
        && !entries.some(([c]) => c === 'USD')
      ) {
        entries.push(['USD', p.max_auto_approve_usd]);
      }
      if (entries.length === 0) return baseDescription;
      const caps = entries
        .map(([code, amount]) => `${code} ≤ ${amount}`)
        .join(', ');
      return `${baseDescription} Your auto-approve ceilings: ${caps}; anything above routes to a human.`;
    }
    case 'r12': {
      const p = rule.params as { mode?: string; countries?: string[] };
      const n = (p.countries ?? []).length;
      const mode = p.mode === 'allow' ? 'allow-list' : 'deny-list';
      return `${baseDescription} Your ${mode} covers ${n} countr${
        n === 1 ? 'y' : 'ies'
      }.`;
    }
    case 'r18': {
      const n = ((rule.params as { manifest_hashes?: string[] })
        .manifest_hashes ?? []).length;
      return `${baseDescription} You have ${n} approved manifest digest${
        n === 1 ? '' : 's'
      }; any other fingerprint is rejected.`;
    }
    case 'r23': {
      const secs = (rule.params as { max_validity_seconds?: number })
        .max_validity_seconds;
      if (typeof secs !== 'number') return baseDescription;
      return `${baseDescription} Your configured maximum is ${secs}s.`;
    }
    case 'r25': {
      const bytes = (rule.params as { max_body_bytes?: number })
        .max_body_bytes;
      if (typeof bytes !== 'number') return baseDescription;
      return `${baseDescription} Your configured limit is ${bytes.toLocaleString()} bytes.`;
    }
    case 'r29': {
      const p = rule.params as {
        require_chain_when_delegated?: boolean;
        max_chain_depth?: number;
        allowed_delegators?: string[];
      };
      const parts: string[] = [];
      if (p.require_chain_when_delegated !== false) {
        parts.push('requires signed chain when delegated');
      }
      if (typeof p.max_chain_depth === 'number') {
        parts.push(`max depth ${p.max_chain_depth}`);
      }
      const headCount = (p.allowed_delegators ?? []).length;
      if (headCount > 0) {
        parts.push(
          `${headCount} approved chain head${headCount === 1 ? '' : 's'}`,
        );
      }
      if (parts.length === 0) return baseDescription;
      return `${baseDescription} Your configuration: ${parts.join('; ')}.`;
    }
    case 'r_custom': {
      // The DSL expression is a merchant-authored string — safe to echo
      // (the merchant wrote it) but bounded so a 4 KB expression doesn't
      // blow up the row. The dashboard rendered the SAME expression in
      // the rule list at `summarizeRuleParams` — this is the same source.
      const expr = (rule.params as { expression?: string }).expression;
      if (typeof expr !== 'string' || expr.length === 0) {
        return baseDescription;
      }
      const trimmed = expr.length > 80 ? `${expr.slice(0, 77)}…` : expr;
      return `${baseDescription} Your expression: ${trimmed}`;
    }
    // No params worth narrowing — let the caller use the catalog
    // description as-is. (r02, r08, r09, r10, r11, r14, r15, r16, r19,
    // r20, r21, r22, r24, r26, r27, r28 fall here.)
    default:
      return null;
  }
}
